import { CSSProperties, FC, useEffect, useState } from 'react';
import { SelectChangeEvent } from '@mui/material/Select';
import { ApiLocaleDef } from '@agunity/api-v4';
import {
  getFullLocale,
  getUnauthorizedLocale,
  setLanguage,
  setLocaleMessage,
  updateLocaleProfile
} from '../../../redux/slices/locales';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { ROOT_PATHS } from '../../../Constants';
import loadMessages, {
  AllLanguageMessages,
  LanguageMessages
} from '../../Language';
import AgLanguageDropdown, {
  LocaleOptionProps
} from '../../../stories/Components/CustomDropdowns/AgLanguageDropdown';
import { defaultLanguage } from '../../../locales/default';

interface Props {
  locale: ApiLocaleDef[];
  expandToLeft?: boolean;
  style?: CSSProperties;
  dataTestId?:string;
}

const Locale: FC<Props> = ({ locale, expandToLeft, dataTestId }) => {
  // en as fallback in case something goes wrong with setting it to browser language
  const [selected, setSelected] = useState<string>();
  const [localeId, setLocaleId] = useState<string>('1');
  const [localMessages, setLocalMessages] = useState<AllLanguageMessages>({});
  const dispatch = useAppDispatch();
  const url = window.location.pathname;
  const localLanguage = useAppSelector((state) => state.language.language);
  const securityToken = useAppSelector(state => state.auth.securityToken);

  const handleChange = (event: SelectChangeEvent | string) => {
    const id = locale.find((l) => l.name === event)?.id;
    if (!id) return;
    setLocaleId(id.toString());
    const language = locale.find(
      (lang: ApiLocaleDef) => lang.id === Number(id)
    );
    if (language?.locale) {
      dispatch(setLanguage(language.locale));
    }
  };

  const fetchLocale = async () => {
    let language = window.navigator.language;
    if (!language || !locale) return;
    const lang = locale.find(
      (item: ApiLocaleDef) => item.locale === localLanguage
    );
    if (!lang?.locale) return;
    try {
      const unAuthLocale = [ROOT_PATHS.RESET_PW, ROOT_PATHS.FORGOT_PW, ROOT_PATHS.ACCOUNT_BLOCK, ROOT_PATHS.ACCESS_TOKEN, ROOT_PATHS.SIGN_UP, ROOT_PATHS.LOGIN];
      const localeFile =
      (unAuthLocale.includes(url) || unAuthLocale.includes(`/${url.split('/')[1]}`))
        ? await dispatch(getUnauthorizedLocale(lang.locale as string)) //since 'lang' has a type, lang.locale needs assertion here
        : await dispatch(getFullLocale(lang.locale as string)); //since 'lang' has a type, lang.locale needs assertion here
      if (localeFile) {
        securityToken && await dispatch(updateLocaleProfile(lang?.locale || defaultLanguage));
        dispatch(
          setLocaleMessage({
            ...localMessages[localLanguage],
            ...(localeFile.data as LanguageMessages), // override by server locale 
          })
        );
        dispatch(setLanguage(lang.locale));
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };
  // const selectStyle = (style && style.color && style.color !== '') ? style.color : 'inherit';

  useEffect(()=>{
    const language = window.navigator.language;
    if (!language || !locale)  return;
    const findLanguage = locale.filter((item) => item.locale === language);
    if (findLanguage.length) {
      const toStringId = findLanguage[0]?.id?.toString();
      if(toStringId)
        setLocaleId(toStringId);
      //TODO when the file of language is generated, change language of this project
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[locale]);

  useEffect(() => {
    loadMessages().then((loadedMessages) => {
      setLocalMessages(loadedMessages);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (locale?.length > 0)
      setSelected(
        locale.find((l) => l.id?.toString() === localeId)?.name || undefined
      );
  }, [locale, localeId]);

  useEffect(() => {
    let language = window.navigator.language;
    if (!language || !locale) return;
    const lang = locale.find(
      (item: ApiLocaleDef) => item.locale === localLanguage
    );
    if (lang?.id) {
      setLocaleId(lang.id.toString());
      return;
    }
    let findLanguage = locale.filter(
      (item: ApiLocaleDef) => item.locale === language
    );

    if (!findLanguage.length) {
      // [GA]: 1st stage fallback: if we don't have "en-GB" I think "en" would be a okay too.
      language = language.split('-')[0];
      findLanguage = locale.filter(
        (item: ApiLocaleDef) => item.locale === language
      );
    }

    if (findLanguage.length && findLanguage[0].id) {
      const toStringId = findLanguage[0].id.toString();
      setLocaleId(toStringId);
    } else if (locale.length && locale[0].id) {
      setLocaleId(locale[0].id.toString()); // set to the id of the first locale in the list
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  useEffect(() => {
    let language = window.navigator.language;
    if (!language || !locale) return;
    const lang = locale.find(
      (item: ApiLocaleDef) => item.locale === localLanguage
    );
    if (!lang?.locale || !localMessages.en) return; //if local file is not loaded, then return, to avoid error in console
    fetchLocale();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, localeId, dispatch, localLanguage, url, localMessages]);

  const localesNames = locale?.map((l) => {
    if (!l) return;
    return { locale: l.name };
  }) as LocaleOptionProps[];

  return (
    <>
      {localeId && (
        <AgLanguageDropdown
          dataTestId={dataTestId}
          localeOptions={localesNames || []}
          setSelected={setSelected}
          onChange={handleChange}
          selected={selected}
          expandToLeft={expandToLeft}
        />
      )}
    </>
  );
};

export default Locale;
